import { apiClient } from "~/services/apiClient";
import type {
  CreateDevicePayload,
  Device,
  DeviceBoundCustomer,
  DeviceGps,
  DeviceListItem,
  DeviceVoiceAsteriskCheck,
  DeviceVoiceCheckResponse,
  DeviceVoiceCredentials,
  DeviceWithVoice,
  UpdateDevicePayload,
  VoiceSync,
} from "~/types/device";
import {
  normalizeVoiceConfigStatus,
  toVoiceSync,
} from "~/types/device";
import type { SearchResponse } from "~/types/search";
import { toSearchMeta } from "~/types/search";

const DEFAULT_WARNINGS = {
  lowBattery: false,
  weakSignal: false,
  checkInTimeout: false,
};

function readString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function readNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function mapBoundCustomers(value: unknown): DeviceBoundCustomer[] {
  if (!Array.isArray(value)) return [];

  const profiles: DeviceBoundCustomer[] = [];
  const seen = new Set<string>();

  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;
    const id = readString(record.id);
    if (!id || seen.has(id)) continue;

    seen.add(id);
    profiles.push({
      id,
      firstName: readString(record.firstName) ?? "",
      lastName: readString(record.lastName) ?? "",
      email: readString(record.email),
      isPrimary: record.isPrimary === true,
    });
  }

  return profiles;
}

function unwrapProfilesPayload(data: unknown): DeviceBoundCustomer[] {
  if (Array.isArray(data)) {
    return mapBoundCustomers(data);
  }

  if (data && typeof data === "object") {
    const record = data as { data?: unknown };
    if (Array.isArray(record.data)) {
      return mapBoundCustomers(record.data);
    }
  }

  return [];
}

function mapCustomerRef(value: unknown): Device["customer"] {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = readString(record.id);
  if (!id) return null;

  return {
    id,
    firstName: readString(record.firstName) ?? "",
    lastName: readString(record.lastName) ?? "",
    email: readString(record.email),
  };
}

function mapUserRef(value: unknown): Device["user"] {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = readString(record.id);
  const email = readString(record.email);
  if (!id || !email) return null;

  return {
    id,
    name: readString(record.name) ?? "",
    surname: readString(record.surname),
    email,
  };
}

function mapWarnings(value: unknown): Device["warnings"] {
  if (!value || typeof value !== "object") return { ...DEFAULT_WARNINGS };
  const record = value as Record<string, unknown>;
  return {
    lowBattery: record.lowBattery === true,
    weakSignal: record.weakSignal === true,
    checkInTimeout: record.checkInTimeout === true,
  };
}

function mapDeviceGps(value: unknown): DeviceGps | null {
  if (!value || typeof value !== "object") return null;

  const record = value as Record<string, unknown>;
  const latitude = readNumber(record.latitude);
  const longitude = readNumber(record.longitude);

  if (latitude == null || longitude == null) return null;

  return {
    latitude,
    longitude,
    lastSeenAt: readString(record.lastSeenAt),
    batteryPct: readNumber(record.batteryPct),
    cellSignal: readNumber(record.cellSignal),
    speedKmh: readNumber(record.speedKmh),
    direction: readNumber(record.direction),
  };
}

function mapVoiceSyncFromRecord(record: Record<string, unknown>): VoiceSync {
  const nested =
    record.voiceSync && typeof record.voiceSync === "object"
      ? (record.voiceSync as Record<string, unknown>)
      : null;

  if (nested) {
    return {
      status: normalizeVoiceConfigStatus(nested.status),
      lastError: readString(nested.lastError),
      provisionedAt: readString(nested.provisionedAt),
    };
  }

  return toVoiceSync({
    voiceConfigStatus: normalizeVoiceConfigStatus(record.voiceConfigStatus),
    voiceLastError: readString(record.voiceLastError),
    voiceProvisionedAt: readString(record.voiceProvisionedAt),
  });
}

/** Normalize API device payloads (list, detail, create, update). */
export function mapDeviceResponse(data: unknown): DeviceWithVoice {
  const record =
    data && typeof data === "object" && "device" in (data as object)
      ? ((data as { device?: unknown }).device as Record<string, unknown>)
      : (data as Record<string, unknown>);

  if (!record || typeof record !== "object") {
    throw new Error("Invalid device response from API.");
  }

  const id = readString(record.id) ?? "";
  const voiceSync = mapVoiceSyncFromRecord(record);
  const sipPassword = readString(record.sipPassword) ?? undefined;

  const device: DeviceWithVoice = {
    id,
    unitId: readString(record.unitId) ?? "",
    nickname: readString(record.nickname),
    imei: readString(record.imei),
    phoneNumber: readString(record.phoneNumber),
    type: (readString(record.type) ?? "OTHER") as Device["type"],
    typeOtherDescription: readString(record.typeOtherDescription),
    status: (readString(record.status) ?? "PENDING") as Device["status"],
    activationDate: readString(record.activationDate) ?? "",
    serialNumber: readString(record.serialNumber),
    lastServiceDate: readString(record.lastServiceDate),
    nextServiceDate: readString(record.nextServiceDate),
    notes: readString(record.notes),
    customerId:
      readString(record.customerId) ?? mapCustomerRef(record.customer)?.id ?? null,
    customer: mapCustomerRef(record.customer),
    boundCustomers: mapBoundCustomers(record.boundCustomers),
    userId: readString(record.userId),
    user: mapUserRef(record.user),
    batteryLevel: readNumber(record.batteryLevel),
    signalStrength: readNumber(record.signalStrength),
    lastCheckIn: readString(record.lastCheckIn),
    gps: mapDeviceGps(record.gps),
    voiceConfigStatus: voiceSync.status,
    voiceProvisionedAt: voiceSync.provisionedAt,
    voiceLastError: voiceSync.lastError,
    voiceLastAttemptAt: readString(record.voiceLastAttemptAt),
    voiceRetryCount: readNumber(record.voiceRetryCount) ?? 0,
    createdAt: readString(record.createdAt) ?? "",
    updatedAt: readString(record.updatedAt) ?? "",
    warnings: mapWarnings(record.warnings),
    voiceSync,
    ...(sipPassword ? { sipPassword } : {}),
  };

  return device;
}

function readBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  return null;
}

function mapAsteriskCheck(value: unknown): DeviceVoiceAsteriskCheck {
  const record =
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

  const checkOk = readBoolean(record.checkOk) ?? false;

  return {
    checkOk,
    configured: readBoolean(record.configured),
    registered: readBoolean(record.registered),
    online: readBoolean(record.online),
    sipUsername: readString(record.sipUsername),
    passwordMatches: readBoolean(record.passwordMatches),
    error: readString(record.error),
  };
}

function mapVoiceCheckResponse(data: unknown): DeviceVoiceCheckResponse {
  const record =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};

  const voiceSync = mapVoiceSyncFromRecord(record);
  const asterisk = mapAsteriskCheck(record.asterisk);

  const sidecarRaw = record.checks;
  const checks =
    sidecarRaw && typeof sidecarRaw === "object"
      ? (sidecarRaw as DeviceVoiceCheckResponse["checks"])
      : undefined;

  const sidecarError =
    checks?.sidecar && typeof checks.sidecar === "object"
      ? readString((checks.sidecar as Record<string, unknown>).error)
      : null;

  const explicitOk = readBoolean(record.ok);
  const ok =
    explicitOk ??
    (asterisk.checkOk ||
      (asterisk.passwordMatches === true && asterisk.registered === true) ||
      voiceSync.status === "SUCCESS");

  return {
    deviceId: readString(record.deviceId) ?? readString(record.id) ?? "",
    unitId: readString(record.unitId) ?? "",
    sipPassword: readString(record.sipPassword),
    voiceSync,
    dbUpdated: readBoolean(record.dbUpdated),
    asterisk,
    ok,
    message:
      readString(record.message) ??
      voiceSync.lastError ??
      sidecarError ??
      asterisk.error,
    checks,
  };
}

function mapVoiceCredentialsResponse(data: unknown): DeviceVoiceCredentials {
  const record =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};

  const deviceId = readString(record.deviceId) ?? readString(record.id) ?? "";
  const unitId = readString(record.unitId) ?? "";
  const sipUsername = readString(record.sipUsername) ?? unitId;
  const sipPassword = readString(record.sipPassword);

  if (!deviceId || !unitId || !sipPassword) {
    throw new Error("Invalid device voice credentials response from API.");
  }

  return {
    deviceId,
    unitId,
    sipUsername,
    sipPassword,
  };
}

function unwrapDeviceList(data: unknown): DeviceListItem[] {
  if (Array.isArray(data)) {
    return data.map((item) => mapDeviceResponse(item));
  }

  if (data && typeof data === "object") {
    const record = data as { data?: unknown; devices?: unknown };

    if (Array.isArray(record.data)) {
      return record.data.map((item) => mapDeviceResponse(item));
    }

    if (Array.isArray(record.devices)) {
      return record.devices.map((item) => mapDeviceResponse(item));
    }
  }

  return [];
}

export function isUnassignedDevice(
  device: Pick<DeviceListItem, "customerId" | "customer">
) {
  const linkedCustomerId =
    readString(device.customerId) ?? mapCustomerRef(device.customer)?.id;
  return !linkedCustomerId;
}

export type DeviceSearchParams = {
  search?: string;
  page?: number;
  limit?: number;
  /** Serialized table filters, see ~/utils/tableFilters. */
  filters?: string;
};

export async function searchDevices(
  params: DeviceSearchParams = {}
): Promise<SearchResponse<DeviceListItem>> {
  const response = await apiClient.get<{
    data?: unknown;
    meta?: unknown;
  }>("/api/devices", { params });
  const data = unwrapDeviceList(response.data);
  return {
    data,
    meta: toSearchMeta(response.data?.meta, {
      query: params.search ?? "",
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      totalCount: data.length,
      resultCount: data.length,
      totalPages: data.length > 0 ? 1 : 0,
    }),
  };
}

/**
 * Values a devices list filter can offer, taken from every device rather than the page on
 * screen.
 */
export async function getDeviceFilterOptions(field: string): Promise<string[]> {
  const response = await apiClient.get<{ data?: unknown }>(
    "/api/devices/filter-options",
    { params: { field } }
  );
  const data = response.data?.data;
  return Array.isArray(data)
    ? data.filter((value): value is string => typeof value === "string")
    : [];
}

/** Fetches all device pages (limit 100) for pickers / secondary lookups. */
export async function getDevices(): Promise<DeviceListItem[]> {
  const pageSize = 100;
  let page = 1;
  let totalPages = 1;
  const all: DeviceListItem[] = [];

  do {
    const response = await searchDevices({ page, limit: pageSize });
    all.push(...response.data);
    totalPages = Math.max(response.meta.totalPages, 1);
    page += 1;
  } while (page <= totalPages);

  return all;
}

export async function getUnassignedDevices(): Promise<DeviceListItem[]> {
  const devices = await getDevices();
  return devices.filter(isUnassignedDevice);
}

export async function linkDeviceToCustomer(
  deviceId: string,
  customerId: string
): Promise<DeviceWithVoice> {
  await bindCustomerToDevice(deviceId, customerId);
  return getDeviceById(deviceId);
}

export async function getDeviceCustomers(
  deviceId: string
): Promise<DeviceBoundCustomer[]> {
  const response = await apiClient.get<unknown>(
    `/api/devices/${encodeURIComponent(deviceId)}/customers`
  );
  return unwrapProfilesPayload(response.data);
}

export async function getDeviceCustomersByUnitId(
  unitId: string
): Promise<DeviceBoundCustomer[]> {
  const response = await apiClient.get<unknown>(
    `/api/devices/by-unit/${encodeURIComponent(unitId)}/customers`
  );
  return unwrapProfilesPayload(response.data);
}

export async function bindCustomerToDevice(
  deviceId: string,
  customerId: string
): Promise<DeviceBoundCustomer[]> {
  const response = await apiClient.post<unknown>(
    `/api/devices/${encodeURIComponent(deviceId)}/customers`,
    { customerId }
  );
  return unwrapProfilesPayload(response.data);
}

export async function unbindCustomerFromDevice(
  deviceId: string,
  customerId: string
): Promise<DeviceBoundCustomer[]> {
  const response = await apiClient.delete<unknown>(
    `/api/devices/${encodeURIComponent(deviceId)}/customers/${encodeURIComponent(customerId)}`
  );
  return unwrapProfilesPayload(response.data);
}

export async function setDevicePrimaryCustomer(
  deviceId: string,
  customerId: string
): Promise<DeviceBoundCustomer[]> {
  const response = await apiClient.put<unknown>(
    `/api/devices/${encodeURIComponent(deviceId)}/customers/${encodeURIComponent(customerId)}/primary`
  );
  return unwrapProfilesPayload(response.data);
}

export async function getDeviceById(id: string): Promise<DeviceWithVoice> {
  const response = await apiClient.get<unknown>(
    `/api/devices/${encodeURIComponent(id)}`
  );
  return mapDeviceResponse(response.data);
}

export async function createDevice(
  payload: CreateDevicePayload
): Promise<DeviceWithVoice> {
  const response = await apiClient.post<unknown>("/api/devices", payload);
  return mapDeviceResponse(response.data);
}

export async function updateDevice(
  id: string,
  payload: UpdateDevicePayload
): Promise<DeviceWithVoice> {
  const response = await apiClient.put<unknown>(
    `/api/devices/${encodeURIComponent(id)}`,
    payload
  );
  return mapDeviceResponse(response.data);
}

/**
 * Unlink a specific customer from a device. When customerId is omitted, unbinds the primary.
 */
export async function unlinkDeviceFromCustomer(
  id: string,
  customerId?: string | null
): Promise<DeviceWithVoice> {
  const device = await getDeviceById(id);
  const targetCustomerId =
    customerId?.trim() ||
    device.customerId ||
    device.customer?.id ||
    device.boundCustomers?.find((profile) => profile.isPrimary)?.id ||
    null;

  if (!targetCustomerId) {
    return device;
  }

  await unbindCustomerFromDevice(id, targetCustomerId);
  return getDeviceById(id);
}

export async function deleteDevice(id: string): Promise<void> {
  await apiClient.delete(`/api/devices/${encodeURIComponent(id)}`);
}

export async function retryDeviceVoice(id: string): Promise<DeviceWithVoice> {
  const response = await apiClient.post<unknown>(
    `/api/devices/${encodeURIComponent(id)}/voice/retry`
  );
  return mapDeviceResponse(response.data);
}

export async function getDeviceVoiceCredentials(
  id: string
): Promise<DeviceVoiceCredentials> {
  const response = await apiClient.get<unknown>(
    `/api/devices/${encodeURIComponent(id)}/voice/credentials`
  );

  return mapVoiceCredentialsResponse(response.data);
}

export async function checkDeviceVoice(
  id: string
): Promise<DeviceVoiceCheckResponse> {
  const response = await apiClient.get<unknown>(
    `/api/devices/${encodeURIComponent(id)}/voice/check`
  );

  return mapVoiceCheckResponse(response.data);
}

export type DeviceAlarmEvent = {
  id: string;
  alarmName: string;
  timestamp: string;
  source: "scaip" | "device";
};

export type DeviceAlarmsResponse = {
  warnings: Device["warnings"];
  events: DeviceAlarmEvent[];
};

function mapDeviceAlarmEvent(value: unknown): DeviceAlarmEvent | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = readString(record.id);
  const alarmName = readString(record.alarmName);
  const timestamp = readString(record.timestamp);
  const source = readString(record.source);
  if (!id || !alarmName || !timestamp) return null;
  if (source !== "scaip" && source !== "device") return null;

  return { id, alarmName, timestamp, source };
}

export async function getDeviceAlarms(deviceId: string): Promise<DeviceAlarmsResponse> {
  const response = await apiClient.get<unknown>(
    `/api/devices/${encodeURIComponent(deviceId)}/alarms`
  );

  const envelope =
    response.data && typeof response.data === "object" && "data" in (response.data as object)
      ? ((response.data as { data?: unknown }).data as Record<string, unknown>)
      : (response.data as Record<string, unknown>);

  const eventsRaw = Array.isArray(envelope?.events) ? envelope.events : [];
  const events = eventsRaw
    .map((item) => mapDeviceAlarmEvent(item))
    .filter((item): item is DeviceAlarmEvent => item != null);

  return {
    warnings: mapWarnings(envelope?.warnings),
    events,
  };
}
